import { getPrimaryParticipant } from "./Prequal/prequalQuotes"
import { participantToLegacyBorrower } from "../DocumentChecklist/applySmartVerifyV2"
import { collectLegacyLiabilities, mapLegacyLoan } from "./legacyLiabilitiesView"

const emptyLoan = () => ({ hash: "" })

const asList = (value) => (Array.isArray(value) ? value : [])

const clonePlainObject = (value) => {
    if (value == null || typeof value !== "object") return null
    try {
        return JSON.parse(JSON.stringify(value))
    } catch (_err) {
        return null
    }
}

/** Legacy Details/LoanEstimate expects fees.prepaids (array) and related scalar fields. */
export const normalizeEstimatedClosingCosts = (primary, fallback) => {
    const ec = clonePlainObject(primary) || clonePlainObject(fallback) || {}
    if (ec.prepaids == null) ec.prepaids = []
    if (ec.initialescrowpaymentsatclosing == null) ec.initialescrowpaymentsatclosing = []
    if (ec.othercosts == null) ec.othercosts = []
    ;[
        "closingcostsfinanced",
        "deposit",
        "fundsforborrower",
        "sellerscredits",
        "adjustmentsothercredits",
        "downpayment",
        "recordingandgovernmentfees",
        "transfertax",
    ].forEach((key) => {
        if (ec[key] == null) ec[key] = 0
    })
    return ec
}

export const cardForClosingCostDetails = (card, selectionEstimatedClosingCosts) => {
    if (!card || typeof card !== "object") return card
    return {
        ...card,
        estimatedclosingcosts: normalizeEstimatedClosingCosts(
            card.estimatedclosingcosts,
            selectionEstimatedClosingCosts,
        ),
    }
}

/**
 * Display-only: align selectedloan.purpose (and purpose-specific funds) with
 * property.purpose in the in-memory Rate Recommendation view. Changing purpose
 * in 1003 updates selection.paydown/downpayment but can leave selectedloan.purpose
 * stale — without this, Rate / Fee Sheet show the wrong Downpayment vs Paydown
 * labels. Does not write to the server.
 */
export const alignSelectedLoanPurpose = (view) => {
    if (!view?.property?.purpose || !view?.selection) {
        return { view, changed: false, selectedloan: null }
    }
    const purpose = view.property.purpose
    const loan = view.selection.selectedloan
    if (!loan || typeof loan !== "object") {
        return { view, changed: false, selectedloan: null }
    }
    const sel = view.selection
    const next = { ...loan, purpose }
    let changed = String(loan.purpose || "") !== String(purpose)
    if (purpose === "purchase") {
        const dp = Number(sel.downpayment) || 0
        if (Number(next.downpayment) !== dp) {
            next.downpayment = dp
            changed = true
        }
    } else if (purpose === "refinance" || purpose === "ratetermrefinance") {
        const pd = Number(sel.paydown) || 0
        if (Number(next.paydown) !== pd) {
            next.paydown = pd
            changed = true
        }
    } else if (purpose === "cashoutrefinance") {
        const co = Number(sel.cashout) || 0
        if (Number(next.cashout) !== co) {
            next.cashout = co
            changed = true
        }
    }
    if (!changed) {
        return { view, changed: false, selectedloan: null }
    }
    return {
        view: {
            ...view,
            selection: { ...view.selection, selectedloan: next },
        },
        changed: true,
        selectedloan: next,
    }
}

const withLegacyBorrowerDefaults = (legacy, base, person, liabilities = {}, primaryPartyId = "") => {
    const occupation = legacy?.occupation && typeof legacy.occupation === "object"
        ? legacy.occupation
        : {}
    const ownedproperties = asList(legacy?.ownedproperties).map((item) => ({
        ...item,
        loans: asList(item?.loans).map((loan) => mapLegacyLoan(loan, primaryPartyId)),
    }))
    const residenceOwnership = person?.residenceOwnership || legacy?.residenceownership || legacy?.presentaddressownership || ""
    return {
        ...base,
        ...legacy,
        presentaddressownership: residenceOwnership,
        presentaddressstreet: legacy?.presentaddressstreet || person?.presentAddress?.street || "",
        occupation: {
            selfemployed: false,
            income: {},
            ...occupation,
            income: occupation.income && typeof occupation.income === "object" ? occupation.income : {},
        },
        otheroccupations: asList(legacy?.otheroccupations),
        previousoccupations: asList(legacy?.previousoccupations),
        ownedproperties,
        income: legacy?.income && typeof legacy.income === "object" ? legacy.income : base.income,
        finances: {
            // The V2 GET returns 2d as person.income.alimony; additionalExpenses only exists in saves.
            alimony: asList(liabilities.additionalExpenses).length ? liabilities.additionalExpenses : asList(person?.income?.alimony).length ? person.income.alimony : legacy?.finances?.alimony || [],
        },
        expenses: {
            ...(base.expenses || {}),
            ...(legacy?.expenses || {}),
            ...(person?.expenses || {}),
            currentrent: person?.monthlyRent || legacy?.monthlyrent || legacy?.expenses?.currentrent || "",
        },
        nonqm: person?.nonqm || legacy?.nonqm || base.nonqm,
        declarations: {
            servearmedforces:
                person?.serveArmedForces
                || person?.declarations?.servearmedforces
                || legacy?.declarations?.servearmedforces
                || base.declarations?.servearmedforces
                || "",
        },
    }
}

export const emptyProductPickerView = () => ({
    firsttimebuyer: false,
    askedforassistance: false,
    scre: 0,
    loanbalance: "",
    product: { mortgageapplied: "" },
    hascoborrower: "",
    borrower: {
        citizenship: "",
        visa: "",
        occupation: { selfemployed: false, income: {}, incomeorloss: "" },
        otheroccupations: [],
        previousoccupations: [],
        expenses: { currentfirstmortgage: "", currentfirstmortgageperiod: "" },
        nonqm: { nonqmincometype: [], nonqmincomeperiod: "monthly", nonqmincomeamount: "" },
        ownedproperties: [],
        declarations: { servearmedforces: "" },
    },
    coborrower: {
        occupation: { selfemployed: false, income: {} },
        otheroccupations: [],
        previousoccupations: [],
        expenses: {},
        nonqm: {},
        ownedproperties: [],
        declarations: { servearmedforces: "" },
    },
    property: {
        purpose: "",
        salesprice: "",
        appraisal: "",
        state: "",
        county: "",
        occupancy: "",
        propertytype: "",
        units: "",
        numberofunits: "",
        hascontract: false,
        loans: [],
    },
    selection: {
        selectedloan: emptyLoan(),
        loSelectedLoans: [],
        downpayment: "",
        paydown: "",
        cashout: "",
        loansize: "",
        mincashout: "",
        loanterms: [],
        loanamortizations: [],
        sortby: "",
        estimatedclosingcosts: {},
        weightclosing: 0,
        weighttotalcost: 0,
        weightmonthly: 0,
    },
    assetsandliabilities: { loans: [], newloans: [] },
    otherliens: [],
    coborrowers: [],
    remotecoborrowers: [],
})

export const adaptV2ToProductPickerView = (application) => {
    const base = emptyProductPickerView()
    if (!application) return base
    const person = getPrimaryParticipant(application)
    const order = Array.isArray(application.participantOrder) ? application.participantOrder : []
    const byId = application.participantsById || {}
    const realEstate = application.finance?.realEstateByPartyId || {}
    const declarationsByPartyId = application.declarationsByPartyId || {}
    const primaryId = application.primaryPartyId || order[0] || ""
    const seenIds = new Set()
    const coborrowerIds = []
    order.concat(Object.keys(byId)).forEach((id) => {
        if (!id || seenIds.has(id) || id === primaryId || !byId[id]) return
        seenIds.add(id)
        coborrowerIds.push(id)
    })
    const coborrowerId = coborrowerIds[0] || ""
    const extraCoborrowerIds = coborrowerIds.slice(1)
    const property = application.property || {}
    const selection = application.selection || {}
    const selectedHash = selection.selectedloan?.hash || selection.selectedloan?._hash || ""
    const selectedloan = selection.selectedloan && typeof selection.selectedloan === "object"
        ? {
            ...selection.selectedloan,
            hash: selectedHash,
            base_rate: selection.selectedloan.base_rate
                ?? selection.selectedloan.Base_rate
                ?? selection.selectedloan._base_rate
                ?? 0,
        }
        : emptyLoan()
    const withDeclarations = (participant, partyId) => ({
        ...(participant || {}),
        declarations: declarationsByPartyId[partyId] || participant?.declarations,
    })
    const toLegacyPerson = (partyId, emptyPerson) => withLegacyBorrowerDefaults(
        participantToLegacyBorrower(byId[partyId], realEstate[partyId]),
        emptyPerson,
        withDeclarations(byId[partyId], partyId),
        application.finance?.liabilitiesByPartyId?.[partyId] || {},
        primaryId,
    )
    const legacyLiabilities = collectLegacyLiabilities(application)
    const selectionEstimatedClosingCosts = normalizeEstimatedClosingCosts(
        selection.estimatedclosingcosts,
        base.selection.estimatedclosingcosts,
    )
    return {
        ...base,
        ...application,
        firsttimebuyer: application.firsttimebuyer,
        askedforassistance: application.askedforassistance,
        scre: person?.estimatedFico || application.scre || 0,
        loanbalance: application.loanbalance || "",
        product: application.product || base.product,
        hascoborrower: coborrowerId ? (application.hascoborrower || "withcoborrower") : (application.hascoborrower || ""),
        borrower: withLegacyBorrowerDefaults(
            participantToLegacyBorrower(person, realEstate[person?.partyId || primaryId]),
            base.borrower,
            withDeclarations(person, person?.partyId || primaryId),
            application.finance?.liabilitiesByPartyId?.[person?.partyId || primaryId] || {},
            primaryId,
        ),
        coborrower: coborrowerId ? toLegacyPerson(coborrowerId, base.coborrower) : base.coborrower,
        coborrowers: extraCoborrowerIds.map((id) => toLegacyPerson(id, base.coborrower)),
        property: {
            ...base.property,
            ...property,
            units: property.numberofunits || property.units || "",
            loans: Array.isArray(property.loans) ? property.loans : [],
        },
        selection: {
            ...base.selection,
            ...selection,
            selectedloan,
            loSelectedLoans: Array.isArray(selection.loSelectedLoans)
                ? selection.loSelectedLoans.map((loan) => ({
                    ...loan,
                    hash: loan?.hash || loan?._hash || "",
                    base_rate: loan?.base_rate ?? loan?.Base_rate ?? loan?._base_rate ?? 0,
                    estimatedclosingcosts: normalizeEstimatedClosingCosts(
                        loan?.estimatedclosingcosts,
                        selectionEstimatedClosingCosts,
                    ),
                }))
                : [],
            loanterms: Array.isArray(selection.loanterms) ? selection.loanterms : [],
            loanamortizations: Array.isArray(selection.loanamortizations) ? selection.loanamortizations : [],
            sortby: selection.sortby || "",
            prepaiddays: selection.prepaiddays ?? null,
            estimatedclosingcosts: selectionEstimatedClosingCosts,
        },
        assetsandliabilities: {
            loans: legacyLiabilities.loans,
            newloans: legacyLiabilities.newloans,
        },
        otherliens: Array.isArray(application.otherliens) ? application.otherliens : [],
        remotecoborrowers: Array.isArray(application.remotecoborrowers) ? application.remotecoborrowers : [],
    }
}
